/**
 * API Client Configuration
 * Axios instance with interceptors for authentication and error handling
 */

import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import axios, { isCancel } from 'axios'
import type Keycloak from 'keycloak-js'

import { ApiError, NetworkError, ValidationError } from '../types/errors'

// Keycloak instance will be set by the KeycloakProvider
let keycloakInstance: Keycloak | null = null

export const setKeycloakInstance = (instance: Keycloak | null) => {
  keycloakInstance = instance
}

const getKeycloakToken = async (): Promise<string | null> => {
  return keycloakInstance?.token || null
}

const isTokenExpired = (): boolean => {
  return keycloakInstance?.isTokenExpired() || false
}

const refreshToken = async (): Promise<void> => {
  if (keycloakInstance) {
    await keycloakInstance.updateToken(30)
  }
}

const logout = async (): Promise<void> => {
  if (keycloakInstance) {
    await keycloakInstance.logout()
  }
}

/**
 * Create Axios instance with base configuration
 */
export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
})

/**
 * Request interceptor: Add JWT token to all requests
 */
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Check if token is expired and refresh if needed
    if (isTokenExpired()) {
      try {
        await refreshToken()
      } catch (error) {
        console.error('Token refresh failed in request interceptor:', error)
        // Continue with request, response interceptor will handle 401
      }
    }

    // Get token and add to Authorization header
    const token = await getKeycloakToken()
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

/**
 * Response interceptor: Handle errors and token refresh on 401
 */
apiClient.interceptors.response.use(
  (response) => response,
  // Axios's error-mapping path: cancel → NetworkError → 401 refresh → 400 field
  // errors → 5xx → generic 4xx. Each HTTP-status branch is a distinct case and
  // flattening them into a map-based dispatcher obscures the control flow.
  // eslint-disable-next-line complexity
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
    }

    // Handle request cancellation silently
    if (error.code === 'ERR_CANCELED' || isCancel(error)) {
      return Promise.reject(error)
    }

    // Handle network errors (no response from server)
    if (!error.response) {
      throw new NetworkError()
    }

    const { status, data } = error.response

    // Handle 401 Unauthorized - attempt token refresh
    if (status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        await refreshToken()
        const token = await getKeycloakToken()

        if (token && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return apiClient(originalRequest)
        }
      } catch (refreshError) {
        // Token refresh failed, logout user
        console.error('Token refresh failed, logging out:', refreshError)
        await logout()
        return Promise.reject(refreshError)
      }
    }

    // Handle validation errors (400 Bad Request)
    if (status === 400 && data && typeof data === 'object') {
      const errorData = data as Record<string, unknown>

      // Check if response contains field-level validation errors
      const hasFieldErrors = Object.keys(errorData).some(
        (key) => Array.isArray(errorData[key])
      )

      if (hasFieldErrors) {
        throw new ValidationError(
          'Validation failed',
          errorData as Record<string, string[]>
        )
      }
    }

    // Handle server errors (500+)
    if (status >= 500) {
      const errorData = data as { message?: string; code?: string }
      throw new ApiError(
        errorData?.message || 'Server error. Please try again later.',
        status,
        errorData?.code
      )
    }

    // Handle other client errors (4xx)
    if (status >= 400 && status < 500) {
      const errorData = data as {
        message?: string
        detail?: string
        code?: string
        details?: Record<string, string[]>
      }
      throw new ApiError(
        errorData?.detail || errorData?.message || 'An error occurred',
        status,
        errorData?.code,
        errorData?.details
      )
    }

    // Generic error fallback
    throw new ApiError('An unexpected error occurred', status)
  }
)

/**
 * Export configured API client
 */
export default apiClient
